import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { usePlans } from '../api/queries';
import type { PlanDto, PlanSyncResult, ServiceKind } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { ServiceBadge } from '../components/status';
import { Badge, Card, ConfirmDialog, Empty, ErrorAlert, Field, Modal, Pagination, Spinner } from '../components/ui';
import { useCurrentRouter } from '../lib/current-router';
import { formatKbps, formatMinutes, formatNumber, formatPrice } from '../lib/format';

interface PlanFormState {
  name: string;
  description: string;
  service: ServiceKind;
  durationMinutes: string;
  validityMinutes: string;
  dataLimitMb: string;
  downloadKbps: string;
  uploadKbps: string;
  sharedUsers: string;
  price: string;
  currency: string;
  mikrotikProfile: string;
  routerId: string;
  isActive: boolean;
}

const EMPTY: PlanFormState = {
  name: '',
  description: '',
  service: 'HOTSPOT',
  durationMinutes: '60',
  validityMinutes: '',
  dataLimitMb: '',
  downloadKbps: '',
  uploadKbps: '',
  sharedUsers: '1',
  price: '0',
  currency: 'USD',
  mikrotikProfile: '',
  routerId: '',
  isActive: true,
};

export function PlansPage() {
  const { hasRole } = useAuth();
  const current = useCurrentRouter();
  // Planes del router de trabajo, más los globales heredados para poder retirarlos.
  const plans = usePlans(current.routerId || undefined, true, undefined, true);
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<PlanDto | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<PlanDto | null>(null);
  /** Segundo paso del borrado: el plan tiene historial y un SUPER_ADMIN puede forzarlo. */
  const [forceDeleting, setForceDeleting] = useState<{ plan: PlanDto; reason: string } | null>(null);
  const [syncResults, setSyncResults] = useState<{ plan: string; results: PlanSyncResult[] } | null>(null);
  const [pageError, setPageError] = useState<unknown>(null);
  const [serviceFilter, setServiceFilter] = useState<'' | 'HOTSPOT' | 'PPPOE'>('');
  const [page, setPage] = useState(1);

  // Otro router, otra lista: la página actual deja de tener sentido.
  useEffect(() => setPage(1), [current.routerId]);

  // El listado de planes llega entero del API (no está paginado allí porque los
  // selectores de otras pantallas lo necesitan completo); aquí se pagina en cliente.
  const PAGE_SIZE = 15;
  const allPlans = (plans.data ?? []).filter(
    (plan) => !serviceFilter || plan.service === serviceFilter,
  );
  const totalPages = Math.max(1, Math.ceil(allPlans.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagePlans = allPlans.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const canManage = hasRole('SUPER_ADMIN');
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['plans'] });

  const sync = useMutation({
    mutationFn: (plan: PlanDto) => api.post<PlanSyncResult[]>(`/plans/${plan.id}/sync`, {}),
    onSuccess: (results, plan) => {
      setPageError(null);
      setSyncResults({ plan: plan.name, results });
    },
    onError: (error) => setPageError(error),
  });

  const remove = useMutation({
    mutationFn: ({ plan, force }: { plan: PlanDto; force?: boolean }) =>
      api.del<void>(`/plans/${plan.id}${force ? '?force=true' : ''}`),
    onSuccess: () => {
      setDeleting(null);
      setForceDeleting(null);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['hotspot-users'] });
      void queryClient.invalidateQueries({ queryKey: ['pppoe-clients'] });
      void queryClient.invalidateQueries({ queryKey: ['vouchers'] });
    },
    onError: (error, variables) => {
      const conflict = error instanceof Error && 'status' in error && (error as { status: number }).status === 409;
      // Con historial el API responde 409; a un SUPER_ADMIN se le ofrece el forzado.
      if (conflict && !variables.force && hasRole('SUPER_ADMIN')) {
        setDeleting(null);
        setForceDeleting({ plan: variables.plan, reason: (error as Error).message });
        return;
      }
      setDeleting(null);
      setPageError(error);
    },
  });

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Planes</h1>
          <p>
            Planes de {current.router?.name ?? 'el router seleccionado'}. Cada plan pertenece a un
            único router y se materializa como un <code>user profile</code> sólo en ese router.
          </p>
        </div>
        {canManage && current.routerId && (
          <button type="button" className="primary" onClick={() => setCreating(true)}>
            Nuevo plan
          </button>
        )}
      </div>

      <ErrorAlert error={pageError} onDismiss={() => setPageError(null)} />

      {syncResults && (
        <div
          className={
            syncResults.results.every((result) => result.ok) ? 'alert ok' : 'alert warn'
          }
        >
          <strong>Sincronización de «{syncResults.plan}»</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {syncResults.results.map((result) => (
              <li key={result.routerId}>
                {result.routerName}: {result.ok ? 'perfil aplicado' : `falló — ${result.error}`}
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="ghost small"
            style={{ marginTop: 8 }}
            onClick={() => setSyncResults(null)}
          >
            Cerrar
          </button>
        </div>
      )}

      <div className="toolbar">
        <select
          value={serviceFilter}
          onChange={(event) => {
            setServiceFilter(event.target.value as '' | 'HOTSPOT' | 'PPPOE');
            setPage(1);
          }}
          style={{ width: 'auto' }}
        >
          <option value="">Ambos servicios</option>
          <option value="HOTSPOT">HotSpot</option>
          <option value="PPPOE">PPPoE</option>
        </select>
        {plans.isFetching && <Spinner label="" />}
      </div>

      <Card
        footer={
          allPlans.length > 0 ? (
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              total={allPlans.length}
              onPage={setPage}
            />
          ) : undefined
        }
      >
        {plans.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : allPlans.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Servicio</th>
                  <th>Velocidad ↓/↑</th>
                  <th className="num">Precio</th>
                  <th className="num">Usuarios</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {pagePlans.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <strong>{plan.name}</strong>{' '}
                      {!plan.isActive && <Badge tone="neutral">inactivo</Badge>}
                      {!plan.routerId && (
                        <span title="Plan de antes de separar los routers: ya no se ofrece en las altas ni se sincroniza. Sus usuarios lo conservan.">
                          <Badge tone="warn">global heredado</Badge>
                        </span>
                      )}
                      <div className="faint" style={{ fontSize: 12 }}>
                        {[
                          plan.service === 'HOTSPOT' && plan.durationMinutes > 0
                            ? formatMinutes(plan.durationMinutes)
                            : null,
                          plan.service === 'HOTSPOT' && plan.dataLimitMb
                            ? `${formatNumber(plan.dataLimitMb)} MB`
                            : null,
                          plan.service === 'HOTSPOT' && plan.sharedUsers > 1
                            ? `${plan.sharedUsers} dispositivos`
                            : null,
                          plan.description,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td>
                      <ServiceBadge service={plan.service} />
                    </td>
                    <td>
                      {plan.downloadKbps || plan.uploadKbps
                        ? `${formatKbps(plan.downloadKbps)} / ${formatKbps(plan.uploadKbps)}`
                        : 'Sin límite'}
                    </td>
                    <td className="num">{formatPrice(plan.price, plan.currency)}</td>
                    <td className="num">
                      {formatNumber(
                        plan.service === 'PPPOE' ? (plan.pppoeClientCount ?? 0) : (plan.hotspotUserCount ?? 0),
                      )}
                    </td>
                    <td className="actions">
                      {canManage && (
                        <>
                          <button
                            type="button"
                            className="small"
                            disabled={sync.isPending || !plan.routerId}
                            onClick={() => sync.mutate(plan)}
                            title={
                              plan.routerId
                                ? `Crea o actualiza el user profile en ${plan.routerName ?? 'su router'}`
                                : 'Un plan global no se sincroniza: pisaría el perfil de todos los routers'
                            }
                          >
                            Sincronizar
                          </button>
                          <button
                            type="button"
                            className="small"
                            style={{ marginLeft: 6 }}
                            onClick={() => setEditing(plan)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="small danger"
                            style={{ marginLeft: 6 }}
                            onClick={() => setDeleting(plan)}
                          >
                            Eliminar
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No hay planes. Crea al menos uno para poder generar usuarios y vouchers.</Empty>
        )}
      </Card>

      {(creating || editing) && (
        <PlanFormModal
          plan={editing}
          router={current.router ? { id: current.router.id, name: current.router.name } : null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(saved) => {
            setCreating(false);
            setEditing(null);
            invalidate();
            // La sincronización automática ya corrió en el backend; si algo falló,
            // se enseña con el mismo banner que el botón «Sincronizar».
            if (saved.syncResults?.some((result) => !result.ok)) {
              setSyncResults({ plan: saved.name, results: saved.syncResults });
            }
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Eliminar «${deleting.name}»`}
          danger
          busy={remove.isPending}
          confirmLabel="Eliminar plan"
          message={
            <p>
              Si el plan no tiene usuarios ni lotes asociados se elimina directamente.
              {hasRole('SUPER_ADMIN')
                ? ' Si tiene historial, se te ofrecerá forzar el borrado en el paso siguiente.'
                : ' Si tiene historial, desactívalo en su lugar: desaparece de los formularios y conserva los registros.'}
            </p>
          }
          onCancel={() => setDeleting(null)}
          onConfirm={() => remove.mutate({ plan: deleting })}
        />
      )}

      {forceDeleting && (
        <ConfirmDialog
          title={`Forzar borrado de «${forceDeleting.plan.name}»`}
          danger
          busy={remove.isPending}
          confirmLabel="Forzar borrado"
          message={
            <>
              <p>{forceDeleting.reason}</p>
              <p>
                Los usuarios, vouchers y clientes asociados <strong>no se borran</strong>: conservan
                su fila y su historial, pero quedarán «sin plan» y no sabrás con qué plan se
                vendieron. El perfil <code>{forceDeleting.plan.mikrotikProfile}</code> del router no
                se toca. Esta operación no se puede deshacer.
              </p>
            </>
          }
          onCancel={() => setForceDeleting(null)}
          onConfirm={() => remove.mutate({ plan: forceDeleting.plan, force: true })}
        />
      )}
    </main>
  );
}

function PlanFormModal({
  plan,
  router,
  onClose,
  onSaved,
}: {
  plan: PlanDto | null;
  /** Router de trabajo: un plan nuevo pertenece siempre a él. */
  router: { id: string; name: string } | null;
  onClose: () => void;
  onSaved: (saved: PlanDto) => void;
}) {
  const [form, setForm] = useState<PlanFormState>(() =>
    plan
      ? {
          name: plan.name,
          description: plan.description ?? '',
          service: plan.service,
          durationMinutes: String(plan.durationMinutes),
          validityMinutes: plan.validityMinutes ? String(plan.validityMinutes) : "",
          dataLimitMb: plan.dataLimitMb ? String(plan.dataLimitMb) : '',
          downloadKbps: plan.downloadKbps ? String(plan.downloadKbps) : '',
          uploadKbps: plan.uploadKbps ? String(plan.uploadKbps) : '',
          sharedUsers: String(plan.sharedUsers),
          price: String(plan.price),
          currency: plan.currency,
          mikrotikProfile: plan.mikrotikProfile,
          routerId: plan.routerId ?? '',
          isActive: plan.isActive,
        }
      : { ...EMPTY, routerId: router?.id ?? '' },
  );
  const [error, setError] = useState<unknown>(null);

  const update = <K extends keyof PlanFormState>(key: K, value: PlanFormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const optionalInt = (value: string) => (value.trim() === '' ? undefined : Number(value));

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        description: form.description || undefined,
        // En PPPoE no hay límite de uso acumulado: la duración no aplica.
        durationMinutes: form.service === 'PPPOE' ? 0 : Number(form.durationMinutes),
        validityMinutes: optionalInt(form.validityMinutes),
        dataLimitMb: optionalInt(form.dataLimitMb),
        downloadKbps: optionalInt(form.downloadKbps),
        uploadKbps: optionalInt(form.uploadKbps),
        sharedUsers: Number(form.sharedUsers),
        price: Number(form.price),
        currency: form.currency.toUpperCase(),
        mikrotikProfile: form.mikrotikProfile || undefined,
        isActive: form.isActive,
      };

      // `routerId` y `service` sólo se definen al crear: cambiarlos después dejaría a
      // los usuarios existentes apuntando a un perfil de otro router u otro menú.
      return plan
        ? api.patch<PlanDto>(`/plans/${plan.id}`, payload)
        : api.post<PlanDto>('/plans', {
            ...payload,
            service: form.service,
            ...(form.routerId ? { routerId: form.routerId } : {}),
          });
    },
    onSuccess: onSaved,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      title={plan ? `Editar «${plan.name}»` : 'Nuevo plan'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Spinner label="Guardando…" /> : 'Guardar'}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      {!plan && (
        <Field
          label="Servicio"
          hint="HotSpot crea el perfil en /ip/hotspot/user/profile; PPPoE en /ppp/profile. No se puede cambiar después."
        >
          <select
            value={form.service}
            onChange={(event) => update('service', event.target.value as ServiceKind)}
          >
            <option value="HOTSPOT">HotSpot (portal cautivo y vouchers)</option>
            <option value="PPPOE">PPPoE (clientes fijos)</option>
          </select>
        </Field>
      )}

      <div className="form-row">
        <Field label="Nombre">
          <input value={form.name} onChange={(event) => update('name', event.target.value)} placeholder="2 Horas" />
        </Field>
        <Field label="Perfil en MikroTik" hint="Vacío = se deriva del nombre">
          <input
            value={form.mikrotikProfile}
            onChange={(event) => update('mikrotikProfile', event.target.value)}
            placeholder="2-horas"
          />
        </Field>
      </div>

      <Field label="Descripción">
        <input value={form.description} onChange={(event) => update('description', event.target.value)} />
      </Field>

      <fieldset>
        <legend>Límites</legend>
        {form.service === 'HOTSPOT' && (
          <div className="form-row">
            <Field label="Tiempo de uso acumulado (minutos)" hint="0 = sin límite; ejemplo: 600 = 10 horas">
              <input
                type="number"
                min={0}
                value={form.durationMinutes}
                onChange={(event) => update('durationMinutes', event.target.value)}
              />
            </Field>
            <Field label="Validez desde el primer uso (minutos)" hint="Vacío/0 = sin caducidad; ejemplo: 4320 = 3 días">
              <input type="number" min={0} value={form.validityMinutes} onChange={(event) => update('validityMinutes', event.target.value)} />
            </Field>
            <Field label="Cuota de datos (MB)" hint="Vacío = sin cuota">
              <input
                type="number"
                min={0}
                value={form.dataLimitMb}
                onChange={(event) => update('dataLimitMb', event.target.value)}
              />
            </Field>
          </div>
        )}

        <div className="form-row">
          <Field label="Bajada (kbps)" hint="Vacío = sin límite">
            <input
              type="number"
              min={0}
              value={form.downloadKbps}
              onChange={(event) => update('downloadKbps', event.target.value)}
              placeholder="10000"
            />
          </Field>
          <Field label="Subida (kbps)">
            <input
              type="number"
              min={0}
              value={form.uploadKbps}
              onChange={(event) => update('uploadKbps', event.target.value)}
              placeholder="5000"
            />
          </Field>
          {form.service === 'HOTSPOT' && (
            <Field label="Dispositivos simultáneos">
              <input
                type="number"
                min={1}
                value={form.sharedUsers}
                onChange={(event) => update('sharedUsers', event.target.value)}
              />
            </Field>
          )}
        </div>
        <p className="faint" style={{ margin: 0, fontSize: 12 }}>
          {form.service === 'PPPOE' ? (
            <>En PPPoE el perfil sólo limita velocidad; la sesión es permanente y única por cliente.</>
          ) : (
            <>
              La duración se aplica como <code>limit-uptime</code> del usuario: es tiempo de uso
              acumulado, no una ventana horaria.
            </>
          )}
        </p>
      </fieldset>

      <fieldset>
        <legend>Comercial</legend>
        <div className="form-row">
          <Field label="Precio">
            <input
              type="number"
              min={0}
              step="0.01"
              value={form.price}
              onChange={(event) => update('price', event.target.value)}
            />
          </Field>
          <Field label="Moneda">
            <input
              value={form.currency}
              maxLength={3}
              onChange={(event) => update('currency', event.target.value.toUpperCase())}
            />
          </Field>
        </div>
      </fieldset>

      {!plan && (
        <p className="faint" style={{ margin: '0 0 12px', fontSize: 12 }}>
          El plan se crea en <strong>{router?.name ?? '—'}</strong>. Para otro router, cámbialo en la
          barra superior.
        </p>
      )}

      <label className="inline">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) => update('isActive', event.target.checked)}
        />
        Activo
      </label>
    </Modal>
  );
}
